import { useRouter } from 'next/router';
import { useRecipient } from '../components/settings/shared/settings-queries';
import { VERIFY_CONNECT, VERIFY_NUMBER } from './phone-verify-steps';
import { recipientNeedsPhone } from './recipient-needs-phone';

/**
 * Whether to ask this caller for a number before handing them something that needs one.
 *
 * Web signup stopped collecting a phone, so an onboarded web user may genuinely have none — a
 * population the native apps do not have (their onboarding phone step is mandatory). Every surface
 * that offers a WhatsApp action asks this one question so they cannot answer it differently.
 *
 * `undefined` while the read is in flight and `null` while the search index still lags a fresh signup
 * both mean *unknown*, not "no phone". Treating unknown as missing puts the ask in front of someone
 * who already gave a number, so the answer is yes only once a recipient has been read AND carries none.
 */
export function useNeedsPhoneConnect(): boolean {
  // Already fetched by the onboarding-entry decision and the rail, so this shares that request.
  const { data: recipient } = useRecipient();
  return recipientNeedsPhone(recipient);
}

/**
 * Opens the verification dialog OVER the current page: "Verify with WhatsApp" first while no phone is on file,
 * the number step otherwise (`PhoneVerifyModal`).
 *
 * The step lives in `?verify=`, so adding the param shallowly opens the dialog without unmounting the
 * route beneath it — and closing strips only that param, returning the caller to the surface they were
 * acting on. That is the whole reason this is not the rail's `/?verify=connect`: the rail is a
 * destination and is going somewhere anyway, where a caller reaching for a button on the Connectors
 * page is mid-task and must land back on it.
 */
export function useOpenPhoneConnect(): () => void {
  return useOpenVerifyStep(VERIFY_CONNECT);
}

/**
 * Opens the NUMBER step over the current page, skipping "Verify with WhatsApp": where that sheet's "Enter my number
 * instead" goes.
 */
export function useOpenPhoneNumberStep(): () => void {
  return useOpenVerifyStep(VERIFY_NUMBER);
}

function useOpenVerifyStep(step: string): () => void {
  const router = useRouter();
  return () => {
    void router.push({ pathname: router.pathname, query: { ...router.query, verify: step } }, undefined, { shallow: true });
  };
}
